import { ReportDkiRMonthValue, ReportDkiRRow } from "./ReportDkiRRow"

/**
 * "Сырая" строка ответа эндпоинта DKI-R (`report_dki_r.py`, `get_dki_r_table_data`).
 *
 * Числовые показатели приходят ПЛОСКИМИ ключами и ТОЛЬКО по активным показателям/месяцам
 * (RQ.eAk.RcDkiR.19/20 — бэкенд сам исключает отключенные показатели/месяцы из ответа):
 * `<indicator>_total` — значение в блоке «Кассовое исполнение, всего»,
 * `<indicator>_MM` — значение за месяц (MM — номер месяца с ведущим нулём, напр. `plan_02`).
 */
export interface ReportDkiRRawRow {
    idx: number
    parent_idx: number | null
    visible: boolean
    collapse: boolean
    hasChild: boolean
    rowLevel: string
    name: string

    region?: string
    gu?: string
    gr?: string
    pgr?: string
    abp?: string
    prg?: string
    ppr?: string
    spf?: string

    // Индекс-сигнатура: plan_total/cash_total/balance_total, plan_01..plan_12 и т.д.
    // (только по активным индикаторам/месяцам — см. описание выше)
    [indicatorField: string]: unknown
}

export interface ReportDkiRRawResponse {
    /** Активные колонки-боковик, напр. ['abp', 'prg', 'ppr', 'spf'] (RQ.eAk.RcDkiR.34) */
    columns: string[]
    /** Активные ключи показателей, напр. ['plan', 'cash', 'balance'] (RQ.eAk.RcDkiR.19) */
    indicators: string[]
    /** Активные месяцы (RQ.eAk.RcDkiR.20) */
    months: number[]
    rows: ReportDkiRRawRow[]
    /**
     * Предупреждение об отсутствии среза кассового исполнения на отчётную дату (RQ.58/RQ.59, §5.4).
     * `null`, если срез на дату `requested` есть. Иначе — `requested` (запрошенная дата) и
     * `available` (дата последнего доступного среза, `null`, если срезов за год нет вообще).
     * Даты — строки `YYYY-MM-DD`. Опционально на случай старых/тестовых ответов без этого поля.
     */
    cashDateWarning?: { requested: string; available: string | null } | null
    /**
     * Номера месяцев, за которые среза нет (уже приходят с пустой кассой в `rows`) —
     * чисто информационное поле, отдельно на фронте не используется (баннер строится из
     * cashDateWarning). Опционально по той же причине, что и cashDateWarning.
     */
    monthsWithoutSnapshot?: number[]
}

const SIDEBAR_KEYS = ['region', 'gu', 'gr', 'pgr', 'abp', 'prg', 'ppr', 'spf'] as const

/**
 * Преобразует "сырую" строку ответа DKI-R (плоские ключи `<indicator>_total`/`<indicator>_MM`,
 * присутствуют только для активных показателей/месяцев) во внутренний `ReportDkiRRow` с массивом
 * `months` (обоснование структуры — см. комментарий в ReportDkiRRow.ts).
 */
export const mapDkiRRow = (
    raw: ReportDkiRRawRow,
    activeIndicators: string[],
    activeMonths: number[],
    index: number
): ReportDkiRRow => {
    const getIndicatorValue = (indicatorKey: string, suffix: string): number | null => {
        if (!activeIndicators.includes(indicatorKey)) {
            return null
        }
        const value = raw[`${indicatorKey}_${suffix}`]
        return value == null ? null : Number(value)
    }

    const months: ReportDkiRMonthValue[] = activeMonths.map(month => {
        const mm = String(month).padStart(2, '0')
        return {
            month,
            plan: getIndicatorValue('plan', mm),
            cash: getIndicatorValue('cash', mm),
            balance: getIndicatorValue('balance', mm)
        }
    })

    const row: ReportDkiRRow = {
        index,
        idx: raw.idx,
        parent_idx: raw.parent_idx,
        hasChild: raw.hasChild,
        visible: raw.visible,
        collapse: raw.collapse,
        rowLevel: raw.rowLevel ?? null,

        region: raw.region ?? null,
        gr: raw.gr ?? null,
        pgr: raw.pgr ?? null,
        abp: raw.abp ?? null,
        gu: raw.gu ?? null,
        prg: raw.prg ?? null,
        ppr: raw.ppr ?? null,
        spf: raw.spf ?? null,

        name: raw.name ?? null,

        plan: getIndicatorValue('plan', 'total'),
        cash: getIndicatorValue('cash', 'total'),
        balance: getIndicatorValue('balance', 'total'),

        months
    }

    return row
}

// Экспортируется для переиспользования (напр. в юнит-тестах qa), чтобы не дублировать список
// ключей колонок-боковика при разборе сырого ответа.
export { SIDEBAR_KEYS }
